import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import useTracking from '../../../../utilities/hooks/useTracking';
import usePageName from '../../../../utilities/common/hooks/usePageName';
import {
  EventActions,
  EventNames,
} from '../../../../shared/constants/analytics';

import PIQuiz from '../../../../assets/OrgDim/pi-quiz-thumbnail.svg';
import faqThumbnail from '../../../../assets/OrgDim/white-paper-thumbnail.png';
import Alert from '../../../widgets/Alert';
import { CircleArrowDown } from '../../../../assets/icons/CircleArrowDown';

const featuredDownloads = [
  {
    title: 'FAQ_FILE_DIMENSIONS',
    subTitle: 'FAQ_FILE_DIMENSIONS_DESC',
    link: `${import.meta.env.VITE_IMAGE_BASE_URL}uploads/Intellect_Dimensions_2_0_FAQs.pdf`,
    instrumentation: 'faq',
    image: faqThumbnail,
  },
  {
    title: 'HOW_EMP_TAKE_PI_QUIZ',
    subTitle: 'HOW_EMP_TAKE_PI_QUIZ_DESC',
    link: `${import.meta.env.VITE_IMAGE_BASE_URL}uploads/dimensions/Accessing personal insights quiz for Dimensions 2.0.pdf`,
    instrumentation: 'pi_quiz_details',
    image: PIQuiz,
  },
];

function DownloadInfoCard() {
  const [expanded, setExpanded] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const { pageName } = usePageName();

  useEffect(() => {
    const container = scrollRef.current;
    if (!container || !expanded) {
      return;
    }

    const onWheel = (event: WheelEvent) => {
      if (event.deltaY === 0) return;
      event.preventDefault();
      container.scrollLeft += event.deltaY;
    };

    container.addEventListener('wheel', onWheel, { passive: false });

    return () => container.removeEventListener('wheel', onWheel);
  }, [expanded]);

  const handleExpandedChange = (nextExpanded: boolean) => {
    setExpanded(nextExpanded);

    if (nextExpanded) {
      trackEvent(EventNames.WellBeingDashboard, {
        eventLabel: 'learn_more',
        eventAction: EventActions.click,
        eventCategory: 'learn_more',
        eventSource: pageName,
      });
    }
  };

  return (
    <Alert
      detailed
      expandable
      icon={null}
      detailedIndicatorLabel="Info"
      className="mb-6"
      variant="info"
      title={t('RESOURCES_LEARN_MORE_HEADING')}
      expandLabel={t('LEARN_MORE')}
      collapseLabel={t('HIDE')}
      expanded={expanded}
      onExpandedChange={handleExpandedChange}
    >
      <div className="space-y-4 pl-2">
        <div className="text-sm font-semibold text-blue-900">
          {t('RESOURCES_LEARN_MORE_HEADING')}
        </div>
        <div
          ref={scrollRef}
          className="-mx-1 flex gap-4 overflow-x-auto pr-3 pb-2 pl-1"
        >
          {featuredDownloads.map((item) => (
            <div
              key={item.title}
              className="border-taupe-550 flex max-w-[500px] flex-shrink-0 items-center rounded-2xl border bg-white"
            >
              <div className="flex flex-col gap-y-3 py-[34px] pr-4 pl-6">
                <div className="text-black-800 text-lg font-bold">
                  {t(item.title)}
                </div>
                <p className="text-black-600 max-w-[320px] text-sm">
                  {t(item.subTitle)}
                </p>
                <Link
                  to={item.link}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-center gap-x-2 text-sm font-medium text-blue-700"
                  onClick={() => {
                    trackEvent(EventNames.WellBeingDashboard, {
                      eventLabel: item.instrumentation,
                      eventCategory: item.instrumentation,
                      eventAction: EventActions.click,
                      eventValue: 'dimensions',
                      eventSource: 'home_page',
                    });
                  }}
                >
                  {t('DOWNLOAD')} {t('FILE')}
                  <CircleArrowDown className="h-5 w-5 stroke-blue-700" />
                </Link>
              </div>
              <img src={item.image} alt="" className="h-full max-h-[160px]" />
            </div>
          ))}
        </div>
      </div>
    </Alert>
  );
}

export default DownloadInfoCard;
